
import { computed, defineComponent, ref } from "vue";
import { useI18n } from "vue-i18n";
import dayjs from "dayjs";

import enUK from "@/locales/invoicing/en-UK.json";

import ClientInvoiceList from "@/components/list/ClientInvoiceList.vue";
import EntityComments from "@/components/EntityComments.vue";
import { GET_CLIENTS_TO_INVOICE } from "@/graphql/invoice/queries";
import {
  ClientToInvoiceReport,
  OrderTimesheetReport,
} from "@/helpers/invoice/typesDefinition";
import { formatPrice } from "@/helpers/utils";
import FormItem from "@/components/form/FormItem.vue";
import MainContentBase from "@/components/layout/MainContentBase.vue";
import BaseSheet from "@/BaseSheet.vue";
import { Icon } from "@iconify/vue";
import AddInvoice from "@/components/CRUXEntity/AddInvoice.vue";

type MessageSchema = typeof enUK;

export default defineComponent({
  setup() {
    const { t } = useI18n<[MessageSchema], "en-UK">({
      useScope: "global",
      inheritLocale: true,
      messages: {
        "en-UK": enUK,
      },
    });

    const clickedItemToInvoice = ref();
    const positiveInvoiceList = ref<{ refresh: () => Promise<unknown> }>();
    const negativeInvoiceList = ref<{ refresh: () => Promise<unknown> }>();
    const toInvoice = ref();
    const toPerform = ref();
    const upUntil = ref(
      dayjs().subtract(1, "month").endOf("month").format("YYYY-MM-DD"),
    );

    const areNegativeVisible = ref(false);
    const switchAreNegativeVisible = () => {
      areNegativeVisible.value = !areNegativeVisible.value;
    };

    const refreshClientsToInvoice = () =>
      Promise.all([
        positiveInvoiceList.value?.refresh(),
        negativeInvoiceList.value?.refresh(),
      ]);

    const sumReports = (
      reports: ClientToInvoiceReport[] | undefined,
      field: keyof ClientToInvoiceReport,
    ) => {
      if (!reports) return 0;
      return reports.reduce(
        (acc, report) => acc + (report[field] as number),
        0,
      );
    };

    const kpis = computed(() => {
      if (!toInvoice.value) return;

      const totalOrdered =
        sumReports(toInvoice.value, "ordersTotal") +
        sumReports(toPerform.value, "ordersTotal");

      const totalPerformed =
        sumReports(toInvoice.value, "performedTimesheetsTotal") +
        sumReports(toPerform.value, "performedTimesheetsTotal") +
        sumReports(toInvoice.value, "totalExpensesNet") +
        sumReports(toPerform.value, "totalExpensesNet");

      const totalToInvoice =
        sumReports(toInvoice.value, "toInvoice") +
        sumReports(toPerform.value, "toInvoice");

      return {
        totalOrdered: {
          label: "totalOrdered", //t("project.budget.label.totalOrdered"),
          value: formatPrice(totalOrdered),
        },
        totalPerformed: {
          label: "totalPerformed", //t("project.budget.label.totalPerformed"),
          value: formatPrice(totalPerformed),
        },
        totalToInvoice: {
          label: "totalToInvoice", //t("project.budget.label.totalToInvoice"),
          value: formatPrice(totalToInvoice),
        },
      };
    });

    return {
      t,
      kpis,
      clickedItemToInvoice,
      positiveInvoiceList,
      negativeInvoiceList,
      refreshClientsToInvoice,
      toInvoice,
      toPerform,
      upUntil,
      areNegativeVisible,
      switchAreNegativeVisible,
    };
  },
  data() {
    return {
      enUK,
      GET_CLIENTS_TO_INVOICE,
      isItemToInvoiceModalVisible: false,
      isCommentsModalVisible: false,
      commentsEntityId: "",
      commentsEntityTitle: "",
    };
  },
  emits: [],
  methods: {
    showItemToInvoiceModal(itemToInvoice: OrderTimesheetReport): void {
      this.clickedItemToInvoice = itemToInvoice;
      this.isItemToInvoiceModalVisible = true;
    },
    closeItemToInvoiceModal(): void {
      this.isItemToInvoiceModalVisible = false;
    },
    showCommentsModal(client: ClientToInvoiceReport): void {
      this.commentsEntityId = client.clientId;
      this.commentsEntityTitle = `Comments for ${client.clientName}`;
      this.isCommentsModalVisible = true;
    },
    closeCommentsModal(): void {
      this.isCommentsModalVisible = false;
    },
  },
  components: {
    AddInvoice,
    BaseSheet,
    MainContentBase,
    Icon,
    FormItem,
    ClientInvoiceList,
    EntityComments,
  },
});
